if (!customElements.get('featured-collection')) {
  customElements.define(
    'featured-collection',
    class FeaturedCollection extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.selectors = {
          sliderWrapper: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items);
        this.tabletItems = parseInt(this.dataset.tabletItems);

        this.sliderInstance = false;

        // Mobile swipe arrows + progress bar work even when the desktop slider is off
        this.initMobileSwipeNav();
        this.initMobileProgressBar();

        if (!this.enableSlider) return;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);
        this.init();
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.destroySlider();
        } else {
          this.initSlider();
        }
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;

        const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);

        // Check for sitewide progress bar element (rendered by Liquid when setting is on)
        const progressBarEl = this.section ? this.section.querySelector('.gh-swiper-scrollbar') : null;

        this.sliderOptions = {
          slidesPerView: this.tabletItems > 3 ? 3 : parseInt(this.tabletItems),
          spaceBetween: columnGap.tablet,
          navigation: {
            nextEl: this.section.querySelector(this.selectors.nextEl),
            prevEl: this.section.querySelector(this.selectors.prevEl),
          },
          pagination: false,
          breakpoints: {
            1024: {
              slidesPerView: this.items > 4 ? 4 : parseInt(this.items),
              spaceBetween: columnGap.tabletLarge,
            },
            1280: {
              slidesPerView: parseInt(this.items),
              spaceBetween: columnGap.desktop,
            },
          },
          loop: false,
          threshold: 2,
        };

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions);
        this.sliderInstance.init();

        // Swiper inits before card images/layout fully settle, so snap points can
        // be stale (navigation appears frozen). Re-measure once layout settles and
        // again on full load so prev/next + loop work reliably.
        const refreshSlider = () => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.update();
          }
        };
        requestAnimationFrame(refreshSlider);
        setTimeout(refreshSlider, 300);
        window.addEventListener('load', refreshSlider, { once: true });

        // Manual progress bar — Scrollbar module is tree-shaken from vendor.js
        if (progressBarEl) {
          this.initProgressBar(progressBarEl);
        }

        this.fixQuickviewDuplicate();

        this.calcNavButtonsPosition();

        window.addEventListener("resize", FoxTheme.utils.debounce(() => {
          this.calcNavButtonsPosition();
        }, 100));
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper.classList.add(this.classes.grid);
        this.enableMobileNavButtons();
        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;
      }

      calcNavButtonsPosition() {
        if (this.dataset.calcButtonPosition !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty(
            '--swiper-navigation-top-offset',
            parseInt(firstMedia.clientHeight) / 2 + 'px'
          );
        }
      }

      calcHeadingColumnHeight() {
        if (this.dataset.calcHeadingColumnHeight !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty('--heading-column-height', firstMedia.clientHeight + 'px');
        }
      }

      enableMobileNavButtons() {
        if (this.dataset.enableSwipeMobile !== 'true' || !FoxTheme.config.mqlMobile || !this.section) return;
        [this.selectors.prevEl, this.selectors.nextEl].forEach((sel) => {
          const btn = this.section.querySelector(sel);
          if (!btn) return;
          btn.classList.remove('swiper-button-disabled', 'swiper-button-lock', 'swiper-button-hidden');
          btn.removeAttribute('aria-disabled');
          btn.style.pointerEvents = 'auto';
        });
      }

      initMobileProgressBar() {
        if (this.dataset.enableSwipeMobile !== 'true' || !this.section) return;
        const trackEl = this.section.querySelector('.gh-swiper-scrollbar');
        if (!trackEl) return;

        let drag = trackEl.querySelector('.swiper-scrollbar-drag');
        if (!drag) {
          drag = document.createElement('div');
          drag.className = 'swiper-scrollbar-drag';
          trackEl.appendChild(drag);
        }

        const updateBar = () => {
          if (!FoxTheme.config.mqlMobile) return;
          const scrollable = this.scrollWidth - this.clientWidth;
          const thumbWidth = Math.min(100, Math.max(10, (this.clientWidth / this.scrollWidth) * 100));
          const progress = scrollable > 0 ? this.scrollLeft / scrollable : 0;
          const maxLeft = 100 - thumbWidth;
          drag.style.display = scrollable > 1 ? 'block' : 'none';
          drag.style.width = (thumbWidth + progress * (100 - thumbWidth)) + '%';
          drag.style.transform = 'none';
        };

        this.addEventListener('scroll', updateBar, { passive: true });
        window.addEventListener('resize', FoxTheme.utils.debounce(updateBar, 100));
        window.addEventListener('load', updateBar, { once: true });
        requestAnimationFrame(updateBar);

        // Draggable on mobile -> drives native scroll position
        let isDragging = false;
        let startX = 0;
        let startScroll = 0;

        const onDragStart = (e) => {
          if (!FoxTheme.config.mqlMobile) return;
          isDragging = true;
          startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
          startScroll = this.scrollLeft;
          e.preventDefault();
        };
        const onDragMove = (e) => {
          if (!isDragging || !FoxTheme.config.mqlMobile) return;
          const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
          const dx = clientX - startX;
          const trackWidth = trackEl.getBoundingClientRect().width;
          const scrollable = this.scrollWidth - this.clientWidth;
          const thumbWidth = (this.clientWidth / this.scrollWidth) * trackWidth;
          const range = Math.max(1, trackWidth - thumbWidth);
          this.scrollLeft = startScroll + (dx / range) * scrollable;
          e.preventDefault();
        };
        const onDragEnd = () => { isDragging = false; };

        drag.addEventListener('mousedown', onDragStart, { passive: false });
        drag.addEventListener('touchstart', onDragStart, { passive: false });
        document.addEventListener('mousemove', onDragMove, { passive: false });
        document.addEventListener('touchmove', onDragMove, { passive: false });
        document.addEventListener('mouseup', onDragEnd);
        document.addEventListener('touchend', onDragEnd);
      }

      initMobileSwipeNav() {
        if (this.dataset.enableSwipeMobile !== 'true' || !this.section) return;

        const prevBtn = this.section.querySelector(this.selectors.prevEl);
        const nextBtn = this.section.querySelector(this.selectors.nextEl);
        if (!prevBtn || !nextBtn) return;

        // Swiper marks the prev button disabled (pointer-events:none) at the first
        // slide; make sure both arrows are clickable when we are on mobile.
        this.enableMobileNavButtons();

        const scrollByColumn = (direction) => {
          const inner = this.sliderWrapper;
          let step = this.clientWidth * 0.7;
          const firstItem = inner && inner.children ? inner.children[0] : null;
          if (firstItem) {
            const gap = parseFloat(getComputedStyle(inner).columnGap) || 0;
            step = firstItem.getBoundingClientRect().width + gap;
          }
          this.scrollBy({ left: direction * step, behavior: 'smooth' });
        };

        // Only act on mobile; on desktop these buttons are driven by Swiper navigation.
        prevBtn.addEventListener('click', () => {
          if (FoxTheme.config.mqlMobile) scrollByColumn(-1);
        });
        nextBtn.addEventListener('click', () => {
          if (FoxTheme.config.mqlMobile) scrollByColumn(1);
        });
      }

      initProgressBar(trackEl) {
        // Create drag thumb element
        const drag = document.createElement('div');
        drag.className = 'swiper-scrollbar-drag';
        trackEl.appendChild(drag);

        const sw = this.sliderInstance.slider;
        const updateBar = () => {
          const total = sw.slides.length;
          let perView = sw.params.slidesPerView;
          if (typeof perView !== 'number' || !isFinite(perView)) {
            perView = (typeof sw.slidesPerViewDynamic === 'function' ? sw.slidesPerViewDynamic() : 1) || 1;
          }
          // progress = ratio of how far through the slides we are
          const progress = sw.progress; // 0..1
          const thumbWidth = Math.min(100, Math.max(10, (perView / Math.max(total, 1)) * 100));
          drag.style.display = total > perView ? 'block' : 'none';
          const maxLeft = 100 - thumbWidth;
          drag.style.width = (thumbWidth + progress * (100 - thumbWidth)) + '%';
          drag.style.transform = 'none';
        };

        sw.on('progress', updateBar);
        sw.on('transitionEnd', updateBar);
        sw.on('breakpoint', updateBar);
        updateBar();

        // Draggable
        let isDragging = false;
        let startX = 0;
        let startProgress = 0;

        const onDragStart = (e) => {
          if (FoxTheme.config.mqlMobile) return;
          isDragging = true;
          startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
          startProgress = sw.progress;
          e.preventDefault();
        };
        const onDragMove = (e) => {
          if (!isDragging) return;
          const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
          const dx = clientX - startX;
          const trackWidth = trackEl.getBoundingClientRect().width;
          const perView = sw.params.slidesPerView || 1;
          const total = sw.slides.length;
          const thumbWidth = (perView / total) * trackWidth;
          const range = trackWidth - thumbWidth;
          const newProgress = Math.min(1, Math.max(0, startProgress + dx / range));
          const targetIdx = Math.round(newProgress * (total - perView));
          sw.slideTo(targetIdx);
          e.preventDefault();
        };
        const onDragEnd = () => { isDragging = false; };

        drag.addEventListener('mousedown', onDragStart, { passive: false });
        drag.addEventListener('touchstart', onDragStart, { passive: false });
        document.addEventListener('mousemove', onDragMove, { passive: false });
        document.addEventListener('touchmove', onDragMove, { passive: false });
        document.addEventListener('mouseup', onDragEnd);
        document.addEventListener('touchend', onDragEnd);
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }
    }
  );
}
